/**
 * 品牌配置上下文
 * 管理系统名称、Logo、Favicon等品牌相关设置
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { BrandingSettings, TenantSettings, getTenantSettings } from '../api/adminMode';
import { getPublicTenantTheme } from '../api/tenantTheme';
import { getToken } from '@/utils/storage';
import { getTenantIdFromContext } from '@/utils/request';
import { ossProxyUrl } from '@/utils/ossUpload';
import { applyTenantFavicon, resolveTenantFaviconUrl } from '@/utils/favicon';

// 默认品牌配置
const DEFAULT_BRANDING: BrandingSettings = {
  system_name: 'Coolbuy 3.0',
  logo_url: '',
  favicon_url: '',
  copyright: '© 2025 Coolbuy. All rights reserved.',
};

interface BrandingContextType {
  branding: BrandingSettings;
  loading: boolean;
  updateBranding: (branding: Partial<BrandingSettings>) => void;
  refreshBranding: () => Promise<void>;
}

export const BrandingContext = createContext<BrandingContextType | null>(null);

interface BrandingProviderProps {
  children: ReactNode;
}

const normalizeBranding = (settings: BrandingSettings): BrandingSettings => {
  const logoUrl = (settings as any).logoUrl || settings.logo_url || '';
  const faviconUrl = (settings as any).faviconUrl || settings.favicon_url || '';
  return {
    ...settings,
    logo_url: ossProxyUrl(logoUrl),
    favicon_url: ossProxyUrl(resolveTenantFaviconUrl(faviconUrl, logoUrl)),
  };
};

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  // 应用品牌设置到 DOM（兼容 keysToCamel 转换后的 camelCase 字段）
  const applyBranding = useCallback((settings: BrandingSettings) => {
    const s = settings as any;
    const systemName = s.systemName || settings.system_name;
    const logoUrl = s.logoUrl || settings.logo_url || '';
    const faviconUrl = ossProxyUrl(
      resolveTenantFaviconUrl(s.faviconUrl || settings.favicon_url || '', logoUrl)
    );
    // 更新页面标题
    if (systemName) {
      document.title = systemName;
    }

    applyTenantFavicon(faviconUrl);
  }, []);

  // 获取品牌配置
  const fetchBranding = useCallback(async () => {
    // 检查用户是否已登录
    const token = getToken();
    if (!token) {
      // 未登录时使用默认配置
      applyBranding(DEFAULT_BRANDING);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getTenantSettings() as unknown as { data: TenantSettings; success: boolean };
      if (response?.success && response.data?.branding) {
        const b = response.data.branding as any;
        // 兼容 keysToCamel 转换后的 camelCase 字段
        const normalized: BrandingSettings = {
          system_name: b.systemName || b.system_name || DEFAULT_BRANDING.system_name,
          logo_url: b.logoUrl || b.logo_url || DEFAULT_BRANDING.logo_url,
          favicon_url: b.faviconUrl || b.favicon_url || DEFAULT_BRANDING.favicon_url,
          copyright: b.copyright || DEFAULT_BRANDING.copyright,
        };
        const merged = normalizeBranding({ ...DEFAULT_BRANDING, ...normalized });

        // 管理员模式下 logo/favicon 显示上游租户品牌（upstream_tenant 的 public theme）
        const tenantId = getTenantIdFromContext();
        if (tenantId) {
          try {
            const themeResp = await getPublicTenantTheme(tenantId);
            if (themeResp.success && themeResp.data) {
              const t = themeResp.data;
              const upstreamLogo = ossProxyUrl(t.logoUrl || '');
              const upstreamFavicon = ossProxyUrl(
                resolveTenantFaviconUrl(t.faviconUrl, t.logoUrl)
              );
              if (upstreamLogo) merged.logo_url = upstreamLogo;
              if (upstreamFavicon) merged.favicon_url = upstreamFavicon;
            }
          } catch {
            // upstream theme失败不阻断主流程
          }
        }

        setBranding(merged);
        applyBranding(merged);
      } else {
        // 使用默认配置
        applyBranding(DEFAULT_BRANDING);
      }
    } catch (error) {
      console.warn('获取品牌配置失败，使用默认配置:', error);
      applyBranding(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [applyBranding]);

  // 更新本地品牌配置（用于预览）
  const updateBranding = useCallback((updates: Partial<BrandingSettings>) => {
    setBranding(prev => {
      const newBranding = normalizeBranding({ ...prev, ...updates });
      applyBranding(newBranding);
      return newBranding;
    });
  }, [applyBranding]);

  // 刷新品牌配置
  const refreshBranding = useCallback(async () => {
    await fetchBranding();
  }, [fetchBranding]);

  // 初始化 + 监听登录状态变化
  useEffect(() => {
    fetchBranding();

    // 监听storage变化（用于跨标签页同步和登录/登出检测）
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'tenant_token') {
        fetchBranding();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchBranding]);

  // 监听同页面的token变化（每5秒检查一次）
  useEffect(() => {
    let lastToken = getToken();
    const interval = setInterval(() => {
      const currentToken = getToken();
      if (currentToken !== lastToken) {
        lastToken = currentToken;
        fetchBranding();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchBranding]);

  return (
    <BrandingContext.Provider
      value={{
        branding,
        loading,
        updateBranding,
        refreshBranding,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingProvider;
